/**
 * Add-On Cart - Auto-add related products (e.g., installation charges)
 * Reads variant metafield custom.add_on_skus (list of SKU/ERP codes)
 * Uses the existing ERPVariantMapper to resolve SKUs to variant IDs.
 *
 * Liquid pre-renders window.__addOnSkusByVariant = { variantId: ["SKU1", "SKU2"], ... }
 * so we can look up add-ons for whichever variant is currently selected.
 */

class AddOnCart {
  constructor() {
    this.debug = true;
    this.listenForVariantChange();
  }

  log(...args) {
    if (this.debug) {
      console.log('[AddOn Cart]', ...args);
    }
  }

  /**
   * Listen for variant changes and update the data-add-on-skus attribute.
   */
  listenForVariantChange() {
    document.addEventListener('change', (e) => {
      const input = e.target.closest('input[name="id"]');
      if (!input) return;

      const variantId = input.value;
      const form = input.closest('sht-prd-frm') || input.closest('form');
      if (!form) return;

      const map = window.__addOnSkusByVariant || {};
      const skus = map[variantId];

      if (skus && skus.length > 0) {
        form.setAttribute('data-add-on-skus', JSON.stringify(skus));
        this.log('Variant changed to', variantId, '- add-on SKUs:', skus);
      } else {
        form.removeAttribute('data-add-on-skus');
        this.log('Variant changed to', variantId, '- no add-on SKUs');
      }
    });
  }

  /**
   * Get the add-on SKUs for the current variant.
   * First tries the data attribute, then falls back to the variant map.
   */
  getAddOnSkus(formElement) {
    // Try data attribute first
    const container = formElement
      ? formElement.closest('sht-prd-frm[data-add-on-skus]') || formElement.closest('[data-add-on-skus]')
      : document.querySelector('sht-prd-frm[data-add-on-skus]') || document.querySelector('[data-add-on-skus]');

    if (container) {
      try {
        const raw = container.getAttribute('data-add-on-skus');
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const skus = parsed.map(s => s.trim()).filter(Boolean);
          this.log('Found add-on SKUs from attribute:', skus);
          return skus;
        }
      } catch (err) {
        console.error('[AddOn Cart] Error parsing add-on SKUs:', err);
      }
    }

    // Fallback: read current variant ID from hidden input and look up in map
    const map = window.__addOnSkusByVariant || {};
    const variantInput = formElement
      ? formElement.closest('form')?.querySelector('input[name="id"]') || document.querySelector('input[name="id"]')
      : document.querySelector('input[name="id"]');

    if (variantInput) {
      const skus = map[variantInput.value];
      if (skus && skus.length > 0) {
        this.log('Found add-on SKUs from variant map:', skus);
        return skus;
      }
    }

    this.log('No add-on SKUs found for current variant');
    return [];
  }

  /**
   * Resolve all add-on SKUs to variant data using erpVariantMapper.
   */
  async resolveAllAddOns(skus) {
    if (!window.erpVariantMapper) {
      console.error('[AddOn Cart] erpVariantMapper not available');
      return [];
    }

    const results = await Promise.all(
      skus.map(async (sku) => {
        try {
          const variant = await window.erpVariantMapper.getVariantByErpCode(sku);
          if (variant && variant.available) {
            return { variant, sku };
          }
          if (variant && !variant.available) {
            console.warn('[AddOn Cart] Variant unavailable for SKU:', sku);
          }
          return null;
        } catch (err) {
          console.warn('[AddOn Cart] Failed to resolve SKU:', sku, err);
          return null;
        }
      })
    );

    return results.filter(Boolean);
  }

  /**
   * Add resolved add-on variants to Shopify cart.
   */
  async addAddOnsToShopifyCart(resolvedAddOns) {
    if (!resolvedAddOns || resolvedAddOns.length === 0) return [];

    const items = resolvedAddOns.map(addon => ({
      id: addon.variant.id,
      quantity: 1,
      properties: {
        _is_add_on: 'true',
        _add_on_sku: addon.sku
      }
    }));

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('[AddOn Cart] Failed to add add-ons to cart:', error);
        return [];
      }

      const data = await response.json();
      this.log('Add-ons added to cart:', data);
      return data.items || [];
    } catch (err) {
      console.error('[AddOn Cart] Error adding add-ons:', err);
      return [];
    }
  }

  /**
   * Sync add-on items to the backend if persistent cart is active.
   */
  async syncAddOnsToBackend(resolvedAddOns) {
    const cart = window.bonitoPersistentCart;
    if (!cart || !cart.getCurrentProject()) {
      this.log('No persistent cart or no project - skipping backend sync for add-ons');
      return;
    }

    const items = resolvedAddOns.map(addon => ({
      erp_code: addon.sku,
      quantity: 1
    }));

    try {
      this.log('Syncing add-ons to backend (bulk):', items);
      await cart.updateCartItemsOnServer(items);
    } catch (err) {
      console.warn('[AddOn Cart] Failed to sync add-ons to backend:', err);
    }
  }

  /**
   * Main entry point: process add-ons for a product being added to cart.
   */
  async processAddOns(formElement) {
    const addOnSkus = this.getAddOnSkus(formElement);
    if (addOnSkus.length === 0) return { added: false, items: [] };

    console.log('[AddOn Cart] Processing', addOnSkus.length, 'add-on(s):', addOnSkus);

    const resolved = await this.resolveAllAddOns(addOnSkus);
    if (resolved.length === 0) {
      console.warn('[AddOn Cart] No add-ons could be resolved');
      return { added: false, items: [] };
    }

    const addedItems = await this.addAddOnsToShopifyCart(resolved);
    await this.syncAddOnsToBackend(resolved);

    console.log('[AddOn Cart] Successfully added', addedItems.length, 'add-on(s)');
    return { added: addedItems.length > 0, items: addedItems };
  }
}

window.addOnCart = new AddOnCart();
console.log('[AddOn Cart] Loaded');